/**
 * ตรรกะล้วนของหน้าจอ Supplier Master — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `desktop/system/departmentsView.ts`: สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้
 * โดยไม่ต้องเรนเดอร์อะไรเลย — โดยเฉพาะ **การประกอบ body** ซึ่งเป็นด่านเดียวที่ทาง
 * เขียนทั้งสองเส้นของหน้าจอนี้เดินผ่าน
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่สร้าง `supplier_id` หรือแก้ `supplier_code` เลย** และ
 * นั่นเป็นข้อจำกัดโดยเจตนา: อันแรกเป็นของเซิร์ฟเวอร์ (D-039) ส่วนอันหลังเป็น
 * business key ที่เขียนครั้งเดียวตอนสร้าง (D-083) — ใบสั่งซื้อ การตรวจรับ และ lot
 * ที่อ้างมันอยู่ต้องอ่านออกต่อไปตลอด
 */

import {
  SUPPLIER_STATUSES,
  type CreateSupplierBody,
  type Supplier,
  type UpdateSupplierBody,
} from './types';

export interface SupplierStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * สามสถานะที่ฟอร์มเสนอให้เลือก — ตามลำดับที่ contract เขียนไว้
 *
 * ทั้งสามไม่ใช่การลบ: **ไม่มี `DELETE` ในกลุ่มนี้เลยโดยตั้งใจ** เอกสารที่อ้างผู้ขาย
 * รายหนึ่งอยู่ต้อง trace กลับมาถึงเขาได้เสมอ ข้อความของแต่ละสถานะจึงต้องพูดว่ามัน
 * **ต่างกันอย่างไร** ไม่ใช่แค่บอกชื่อ — โดยเฉพาะ `INACTIVE` กับ `BLOCKED` ซึ่งอ่าน
 * เผิน ๆ เหมือนกัน แต่อันหนึ่งคือความสัมพันธ์ที่จบไปแล้ว ส่วนอีกอันคือความสัมพันธ์
 * ที่ยังอยู่แต่ถูกระงับไว้
 */
export const SUPPLIER_STATUS_DESCRIPTORS: readonly SupplierStatusDescriptor[] = [
  {
    code: 'ACTIVE',
    label: 'ใช้งานอยู่ (ACTIVE)',
    summary: 'ออกใบสั่งซื้อและรับของจากผู้ขายรายนี้ได้ตามปกติ',
  },
  {
    code: 'INACTIVE',
    label: 'เลิกใช้แล้ว (INACTIVE)',
    summary:
      'ไม่ใช่การลบ — เอกสารเดิมที่อ้างผู้ขายรายนี้ยังอ่านออกและตามสอบได้ครบทุกใบ',
  },
  {
    code: 'BLOCKED',
    label: 'ระงับไว้ (BLOCKED)',
    summary:
      'คนละความหมายกับเลิกใช้ — ความสัมพันธ์ยังอยู่แต่ถูกระงับไว้ การห้ามออกใบสั่งซื้อใหม่บังคับที่หน้าจัดซื้อ (P2-2) ไม่ใช่ที่นี่',
  },
];

/** สีของป้ายสถานะ — `ACTIVE` เท่านั้นที่เป็นสถานะ "ใช้งานได้" */
export type SupplierStatusTone = 'success' | 'neutral' | 'danger';

export function supplierStatusTone(status: string): SupplierStatusTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'BLOCKED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น** */
export function supplierStatusLabel(status: string): string {
  return SUPPLIER_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status;
}

/**
 * สถานะที่กล่องแก้ไขเสนอให้เลือกสำหรับผู้ขายรายหนึ่ง
 *
 * ปกติคือสามค่าของ contract แต่ถ้าผู้ขายรายนั้นถือสถานะที่หน้าจอยังไม่รู้จัก
 * (เช่นเซิร์ฟเวอร์ขยาย CHECK constraint ไปแล้วแต่หน้าจอยังไม่ตาม) ค่านั้นจะถูก
 * **เติมเข้าไปในตัวเลือกด้วย** ไม่ใช่ถูกละทิ้ง — ไม่งั้นการเปิดกล่องเพื่อแก้ชื่อ
 * อย่างเดียวจะทำให้ค่าที่เลือกไว้เด้งไปเป็น `ACTIVE` เงียบ ๆ แล้วผู้ใช้กดบันทึก
 * ปลดการระงับผู้ขายที่เขาไม่ได้ตั้งใจแตะ
 */
export function offeredSupplierStatuses(currentStatus: string): readonly string[] {
  const statuses: readonly string[] = SUPPLIER_STATUSES;
  return statuses.includes(currentStatus) ? statuses : [...statuses, currentStatus];
}

/**
 * กรองรายการผู้ขายด้วยคำค้นเดียว — เทียบทั้งรหัสและชื่อเพราะตารางแสดงทั้งสองคอลัมน์
 *
 * เจ้าหน้าที่จัดซื้อที่คัดลอกรหัสผู้ขายมาจากใบสั่งซื้อต้องหาผู้ขายรายนั้นเจอด้วยรหัสนั้น
 * ไม่ใช่ต้องแปลรหัสกลับเป็นชื่อก่อน
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/suppliers` ไม่มีพารามิเตอร์ค้นหาเลย มีแต่ `status`/`limit`/`cursor`
 */
export function filterSuppliers(
  items: readonly Supplier[],
  query: string,
): readonly Supplier[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (supplier) =>
      supplier.supplier_code.toLocaleLowerCase().includes(needle) ||
      supplier.supplier_name.toLocaleLowerCase().includes(needle),
  );
}

/**
 * ประกอบ body ของ `POST /v1/suppliers` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ทั้งรหัสและชื่อถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอ
 * แต่ **ติดอยู่กับ business key ตลอดไป**: `supplier_code` แก้ไม่ได้หลังสร้าง (D-083)
 * รหัสที่มีช่องว่างติดมาจากการคัดลอกจึงกลายเป็นรหัสที่พิมพ์ตามไม่ได้ และเป็นรหัสที่
 * `uq` มองว่าไม่ซ้ำกับรหัสเดียวกันที่ไม่มีช่องว่าง
 *
 * `status` ที่เป็นสตริงว่างกลายเป็น `undefined` **ไม่ใช่สตริงว่างที่ถูกส่งออกไป** —
 * contract บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้
 */
export function toCreateSupplierBody(
  supplierCode: string,
  supplierName: string,
  status: string,
): CreateSupplierBody | null {
  const code = supplierCode.trim();
  const name = supplierName.trim();
  if (code === '' || name === '') {
    return null;
  }
  const trimmedStatus = status.trim();
  return {
    supplier_code: code,
    supplier_name: name,
    ...(trimmedStatus === '' ? {} : { status: trimmedStatus }),
  };
}

/**
 * ประกอบ body ของ `PATCH /v1/suppliers/{supplier_id}` — **เฉพาะสิ่งที่เปลี่ยนจริง**
 *
 * คืน `null` ในสองกรณีที่ต่างกันแต่ให้ผลเดียวกันคือ "ยังไม่มีคำขอให้ส่ง":
 *  1. ชื่อว่างเปล่า — คอลัมน์นี้ `NOT NULL` และเป็นสิ่งเดียวที่คนอ่านใบสั่งซื้อใช้
 *     รู้ว่ากำลังซื้อจากใคร
 *  2. ไม่มีอะไรเปลี่ยนจากค่าที่โหลดมา — เซิร์ฟเวอร์ยินดีรับ body ว่างและจะเขียน
 *     `audit_log` action `UPDATE` ให้ (contract ระบุว่าเขียน "เสมอ แม้ไม่มีอะไร
 *     เปลี่ยน") แต่คำขอที่ไม่ได้ขออะไรเลยคือแถว audit ที่บันทึกว่าไม่มีการเปลี่ยน
 *     แปลง ซึ่งทำให้สมุดที่ผู้ตรวจสอบใช้อ่านยากขึ้นโดยไม่ได้อะไรกลับมา
 *
 * `supplier_code` ไม่มีทางเข้ามาถึงที่นี่ได้เลย — มันไม่ใช่พารามิเตอร์ของฟังก์ชันนี้
 * และไม่ใช่คีย์ของ `UpdateSupplierBody`
 */
export function toUpdateSupplierBody(
  current: Supplier,
  supplierName: string,
  status: string,
): UpdateSupplierBody | null {
  const name = supplierName.trim();
  if (name === '') {
    return null;
  }
  const trimmedStatus = status.trim();

  const body: UpdateSupplierBody = {
    ...(name === current.supplier_name ? {} : { supplier_name: name }),
    ...(trimmedStatus === '' || trimmedStatus === current.status
      ? {}
      : { status: trimmedStatus }),
  };

  return body.supplier_name === undefined && body.status === undefined ? null : body;
}
